/**
 * 设置抽屉组件
 */

import React from 'react';
import {
  Drawer,
  Space,
  Typography,
  Divider,
  Switch,
  Select,
  Slider,
  Button,
  Row,
  Col,
  Card,
  message,
} from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import useTheme from '@/hooks/useTheme';
import { useI18n } from '@/hooks/useI18n';
import { presetColors } from '@/utils/theme';
import { supportedLanguages } from '@/locales';
import ThemeSwitcher from '../ThemeSwitcher';
import LanguageSwitcher from '../LanguageSwitcher';

const { Title, Text } = Typography;
const { Option } = Select;

interface SettingsDrawerProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * 设置抽屉组件
 */
const SettingsDrawer: React.FC<SettingsDrawerProps> = ({ visible, onClose }) => {
  const {
    themeConfig,
    setThemeConfig,
    setPrimaryColor,
    setCompactMode,
    resetTheme
  } = useTheme();
  const { t, changeLanguage, language } = useI18n();

  // Temporary local state for layout settings (replace with your actual state management)
  const [layoutSettings, setLayoutSettings] = React.useState({
    fixedHeader: false,
    fixedSidebar: false,
    showTabs: true,
    showBreadcrumb: true,
    showFooter: true,
    weakMode: false,
    colorBlind: false,
  });

  const updateLayoutSettings = (changes: Partial<typeof layoutSettings>) => {
    setLayoutSettings(prev => ({ ...prev, ...changes }));
  };

  // 重置所有设置
  const handleResetAll = () => {
    resetTheme();
    message.success(t('message.operationSuccess'));
  };

  // 渲染主题设置
  const renderThemeSettings = () => (
    <Card size="small" title={t('settings.theme')}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* 主题模式切换 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.theme')}</Text>
          </Col>
          <Col>
            <ThemeSwitcher type="icon" showColorPicker={false} />
          </Col>
        </Row>

        {/* 主色选择 */}
        <div>
          <Text>{t('settings.primaryColor')}</Text>
          <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
            {Object.entries(presetColors).map(([name, color]) => (
              <Col key={name}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    backgroundColor: color,
                    border: themeConfig.primaryColor === color ? '2px solid #000' : '1px solid #d9d9d9',
                    cursor: 'pointer',
                  }}
                  onClick={() => setPrimaryColor(color)}
                  title={name}
                />
              </Col>
            ))}
          </Row>
        </div>

        {/* 圆角大小 */}
        <div>
          <Text>{t('settings.borderRadius')}</Text>
          <Slider
            min={0}
            max={16}
            value={themeConfig.borderRadius}
            onChange={(value) => setThemeConfig({ borderRadius: value })}
            style={{ marginTop: 8 }}
          />
        </div>

        {/* 字体大小 */}
        <div>
          <Text>{t('settings.fontSize')}</Text>
          <Slider
            min={12}
            max={18}
            value={themeConfig.fontSize}
            onChange={(value) => setThemeConfig({ fontSize: value })}
            style={{ marginTop: 8 }}
          />
        </div>

        {/* 紧凑模式 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.compactMode')}</Text>
          </Col>
          <Col>
            <Switch
              checked={themeConfig.compactMode}
              onChange={setCompactMode}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );

  // 渲染布局设置
  const renderLayoutSettings = () => (
    <Card size="small" title={t('settings.layout')}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* 固定头部 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.fixedHeader')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.fixedHeader}
              onChange={(checked) => updateLayoutSettings({ fixedHeader: checked })}
            />
          </Col>
        </Row>

        {/* 固定侧边栏 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.fixedSidebar')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.fixedSidebar}
              onChange={(checked) => updateLayoutSettings({ fixedSidebar: checked })}
            />
          </Col>
        </Row>

        {/* 显示标签页 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.showTabs')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.showTabs}
              onChange={(checked) => updateLayoutSettings({ showTabs: checked })}
            />
          </Col>
        </Row>

        {/* 显示面包屑 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.showBreadcrumb')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.showBreadcrumb}
              onChange={(checked) => updateLayoutSettings({ showBreadcrumb: checked })}
            />
          </Col>
        </Row>

        {/* 显示页脚 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.showFooter')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.showFooter}
              onChange={(checked) => updateLayoutSettings({ showFooter: checked })}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );

  // 渲染语言设置
  const renderLanguageSettings = () => (
    <Card size="small" title={t('settings.language')}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.language')}</Text>
          </Col>
          <Col>
            <LanguageSwitcher type="icon" />
          </Col>
        </Row>
        
        <Select
          style={{ width: '100%' }}
          value={language}
          onChange={changeLanguage}
        >
          {supportedLanguages.map(lang => (
            <Option key={lang.key} value={lang.key}>
              <Space>
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </Space>
            </Option>
          ))}
        </Select>
      </Space>
    </Card>
  );

  // 渲染其他设置
  const renderOtherSettings = () => (
    <Card size="small" title={t('settings.other')}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* 色弱模式 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.weakMode')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.weakMode}
              onChange={(checked) => updateLayoutSettings({ weakMode: checked })}
            />
          </Col>
        </Row>

        {/* 色盲模式 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text>{t('settings.colorBlind')}</Text>
          </Col>
          <Col>
            <Switch
              checked={layoutSettings.colorBlind}
              onChange={(checked) => updateLayoutSettings({ colorBlind: checked })}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );

  return (
    <Drawer
      title={
        <Space>
          <SettingOutlined />
          {t('settings.title')}
        </Space>
      }
      placement="right"
      width={320}
      open={visible}
      onClose={onClose}
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={handleResetAll}
          title={t('settings.reset')}
        />
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {renderThemeSettings()}
        <Divider />
        {renderLayoutSettings()}
        <Divider />
        {renderLanguageSettings()}
        <Divider />
        {renderOtherSettings()}
        
        <Button
          type="primary"
          danger
          block
          onClick={handleResetAll}
          style={{ marginTop: 24 }}
        >
          {t('settings.reset')}
        </Button>
      </Space>
    </Drawer>
  );
};

export default SettingsDrawer;